//#region Imports
import React, { useState, useEffect } from 'react';
import { Puc } from '../models/puc';
import CabinetRenderer from './cabinet-renderer';
import AddPucDialog from './dialogs/add-puc-dialog';
import { 
  Alert,
  AlertTitle,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Drawer,
  Fab,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Link,
  List,
  ListItem,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import { Locker } from '../models/locker';
import { Board } from '../models/board';
import { Screen } from '../models/screen';
import { Door } from '../models/door';
import {
  getSlugId,
} from '../utilities/model-utils';
//#endregion

//#region Custom Props
interface PucEditorProps {
  pucs: Array<Puc>,
  canvasWidth: number,
  canvasHeight: number,
  applyHardwareChanges(changedHardware: Array<Puc>): void
}

const item = {
  py: '2px',
  px: 3,
  marginTop: '15px',
  color: 'rgba(255, 255, 255, 0.7)',
  '&:hover, &:focus': {
    bgcolor: 'rgba(255, 255, 255, 0.08)',
  },
};

const itemCategory = {
  boxShadow: '0 -1px 0 rgb(255,255,255,0.1) inset',
  py: 1.5,
  px: 3,
};
//#endregion

function PucEditor({ pucs, applyHardwareChanges, canvasWidth, canvasHeight }: PucEditorProps) {
  //#region Hardware Model state
  const [forceSelectMain, setForceSelectMain] = useState<boolean>(false);
  const [dialog, setDialog] = useState<boolean>(false);
  const [hardwareChanged, setHardwareChanged] = useState<number>(0);
  const [lockers, setLockers] = useState<Array<Locker>>([]);
  const [boards, setBoards] = useState<Array<Board>>([]);
  const [screens, setScreens] = useState<Array<Screen>>([]);
  //#endregion

  //#region Render state members
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  //#endregion

  //#region Puc Editor state members
  const [selectedPuc, setSelectedPuc] = useState<Puc>();
  const [selectedPucId, setSelectedPucId] = useState<string>('');
  //#endregion

  //#region Screen Editor state members
  const [selectedScreen, setSelectedScreen] = useState<Screen>();
  const [selectedScreenId, setSelectedScreenId] = useState<string>('');
  //#endregion

  //#region Board Editor state members
  const [selectedBoard, setSelectedBoard] = useState<Board>();
  const [selectedBoardId, setSelectedBoardId] = useState<string>('');
  //#endregion
  
  //#region Locker Editor state members
  const [selectedLocker, setSelectedLocker] = useState<Locker>();
  const [selectedLockerId, setSelectedLockerId] = useState<string>('');
  const [selectedLockerLabel, setSelectedLockerLabel] = useState<string>('');
  const [selectedLockerFrontDoor, setSelectedLockerFrontDoor] = useState<boolean>(true);
  const [selectedLockerBackDoor, setSelectedLockerBackDoor] = useState<boolean>(false);
  //#endregion

  //#region Utility Methods
  const showDeletePopup = (show: boolean) => {
    setShowDeleteDialog(show);
  };
  
  const handleDelete = () => {
    if (selectedPucId) {
      const tempPucs = pucs.filter(x => x.id !== selectedPucId);
      pucs = tempPucs;
      setSelectedPucId('');
    } else if (selectedBoardId) {
      const tempBoards = boards.filter(x => x.id !== selectedBoardId);
      setBoards(tempBoards);
      const puc = pucs.find(x => x.id === selectedBoard?.puc_id);
      puc!.boards = tempBoards;
      setSelectedBoardId('');
    } else if (selectedScreenId) {
      const tempScreens = screens.filter(x => x.id.toString() !== selectedScreenId);
      setScreens(tempScreens);
      const puc = pucs.find(x => x.id === selectedScreen?.puc_id);
      puc!.screens = tempScreens;
      setSelectedScreenId('');
    } else if (selectedLockerId) {
      const board = boards.find(x => x.id === selectedLocker?.board_id);
      const boardLockers = board!.lockers.filter(x => x.id !== selectedLockerId);
      board!.lockers = boardLockers;
      setSelectedLockerId('');
    }
    flagHardwareChange();
    setShowDeleteDialog(false);
  };
  
  const flagHardwareChange = () => {
    applyHardwareChanges(pucs);
    setHardwareChanged(hardwareChanged+1);
  };

  const showError = (message: string) => {
    setErrorMessage(message);
    setTimeout(() => {
      setErrorMessage('');
    }, 3000);
  };

  const validateDimensions = (conversion: number) => {
    if (Number.isNaN(conversion) || conversion < 0.0) {
      return false;
    }
    return true;
  };
  //#endregion

  //#region Puc Editing
  const pucSerialChanged = (serial: string) => {
    if (!serial) {
      showError('You must input a serial value');
    } else {
      selectedPuc!.serial = serial;
      flagHardwareChange();
    }
  };
  const pucOriginXChanged = (x: string) => {
    const conversion = parseFloat(x);
    if (validateDimensions(conversion)) {
      selectedPuc!.origin_x_cm = conversion;
      flagHardwareChange();
    } else {
      showError('You must input a positive number for X origin');
    }
  };
  const pucOriginYChanged = (y: string) => {
    const conversion = parseFloat(y);
    if (validateDimensions(conversion)) {
      selectedPuc!.origin_y_cm = conversion;
      flagHardwareChange();
    } else {
      showError('You must input a positive number for Y origin');
    }
  };
  const pucWidthChanged = (width: string) => {
    const conversion = parseFloat(width);
    if (validateDimensions(conversion) && conversion > 0.0) {
      selectedPuc!.width_cm = conversion;
      flagHardwareChange();
    } else {
      showError('You must input a number greater than 0.0 for width');
    }
  };
  const pucHeightChanged = (height: string) => {
    const conversion = parseFloat(height);
    if (validateDimensions(conversion) && conversion > 0.0) {
      selectedPuc!.height_cm = conversion;
      flagHardwareChange();
    } else {
      showError('You must input a number greater than 0.0 for height');
    }    
  };
  //#endregion

  //#region Board Editing
  const boardSerialChanged = (newSerial: string) => {
    if (!newSerial) {
      showError('Serial is required');
      return;
    }
    selectedBoard!.serial = newSerial;
    flagHardwareChange();
  };

  const boardSlaveAddressChanged = (newAddress: string) => {
    const conversion = parseInt(newAddress);
    if (Number.isNaN(conversion) || conversion < 0) {
      showError('Slave address is required');
      return;
    }

    const indeces = boards.map(x => x.slave_address);
    if (indeces.includes(conversion)) {
      showError(`Slave Address ${conversion} already takien. Choose another`);
    } else {
      selectedBoard!.slave_address = conversion;
      flagHardwareChange();
    }
  };
  const boardModelChanged = (newModel: string) => {
    if (!newModel) {
      showError('Model is required');
      return;
    }
    selectedBoard!.model = newModel;
    flagHardwareChange();
  };
  const boardProductSensorChanged = (hasProductSensors: boolean) => {
    selectedBoard!.lockers_have_product_sensor = hasProductSensors;
    flagHardwareChange();
  };
  const boardMotorizedChanged = (hasMotorizedClose: boolean) => {
    selectedBoard!.lockers_have_motorized_close = hasMotorizedClose;
    flagHardwareChange();
  };
  //#endregion

  //#region Locker Editing
  const changeLockerLabel = (newLabel: string) => {
    const locker = lockers.find(x => x.id === selectedLockerId);
    setSelectedLockerLabel(newLabel);
    locker!.label = newLabel;
    flagHardwareChange();    
  };

  const lockerOriginXChanged = (x: string) => {
    const conversion = parseFloat(x);
    if (validateDimensions(conversion)) {
      selectedLocker!.origin_x_cm = conversion;
      flagHardwareChange();
    } else {
      showError('You must input a positive number for X origin');
    }
  };
  const lockerOriginYChanged = (y: string) => {
    const conversion = parseFloat(y);
    if (validateDimensions(conversion)) {
      selectedLocker!.origin_y_cm = conversion;
      flagHardwareChange();
    } else {
      showError('You must input a positive number for Y origin');
    }
  };
  const lockerWidthChanged = (width: string) => {
    const conversion = parseFloat(width);
    if (validateDimensions(conversion) && conversion > 0.0) {
      selectedLocker!.width_cm = conversion;
      flagHardwareChange();
    } else {
      showError('You must input a number greater than 0.0 locker for width');
    }
  };
  const lockerHeightChanged = (height: string) => {
    const conversion = parseFloat(height);
    if (validateDimensions(conversion) && conversion > 0.0) {
      selectedLocker!.height_cm = conversion;
      flagHardwareChange();
    } else {
      showError('You must input a number greater than 0.0 for height');
    }    
  };
  const lockerTypeChanged = (lockerType: string) => {
    selectedLocker!.type = lockerType;
    flagHardwareChange();
  };

  const changeHardwareIndex = (newIndex: string) => {
    const conversion = parseInt(newIndex);
    if (Number.isNaN(conversion)) {
      showError('Hardware index must be a number');
    } else if (conversion < 0) {
      showError('Hardware index must be positive');
    } else if (conversion < 0 || conversion > 4) {
      showError('Hardware index must be in the range of 0 to 4');
    } else {
      const board = boards.find(x => x.id === selectedLocker!.board_id);
      const indeces = board!.lockers.map(x => x.hardware_index);
      if (indeces.includes(conversion)) {
        const foundLocker = board!.lockers.find(x => x.hardware_index === conversion);
        showError(`Hardware index ${conversion} is already taken locker ${foundLocker!.label}.`);
      } else {
        selectedLocker!.hardware_index = Number.parseInt(newIndex);
        flagHardwareChange();
        return;
      }
    }
  };

  const enableChanged = (enabled: boolean) => {
    selectedLocker!.enabled = enabled;
    flagHardwareChange();
  };

  const accessibleChanged = (accessible: boolean) => {
    selectedLocker!.accessible = accessible;
    flagHardwareChange();
  };
  
  const doorsModified = (enabled: boolean, location: string) => {
    const existingDoor = selectedLocker!.doors.findIndex(x => x.location === location);
    const newDoor = new Door();
    newDoor.id = getSlugId();
    newDoor.locker_id = selectedLockerId;
    newDoor.location = location;
    switch (location) {
      case 'front':
        if (!enabled && !selectedLockerBackDoor) {
          showError('Each locker needs at least 1 door');
        } else {
          setSelectedLockerFrontDoor(enabled);
          if (existingDoor !== -1 && !enabled) {
            selectedLocker?.doors.splice(existingDoor, 1);
          } else if (existingDoor === -1 && enabled) {
            selectedLocker!.doors.push(newDoor);
          }
        }
        break;
      case 'back':
        if (!enabled && !selectedLockerFrontDoor) {
          showError('Each locker needs at least 1 door');
        } else {
          setSelectedLockerBackDoor(enabled);
          if (existingDoor !== -1 && !enabled) {
            selectedLocker?.doors.splice(existingDoor, 1);
          } else if (existingDoor === -1 && enabled) {
            selectedLocker!.doors.push(newDoor);
          }
        }
        break;
    }
  };
  //#endregion
  
  //#region Screen Editing
  const screenOriginXChanged = (x: string) => {
    const conversion = parseFloat(x);
    if (validateDimensions(conversion)) {
      selectedScreen!.origin_x_cm = conversion;
      
      flagHardwareChange();
    } else {
      showError('You must input a positive number for X origin');
    }
  };
  const screenOriginYChanged = (y: string) => {
    const conversion = parseFloat(y);
    if (validateDimensions(conversion)) {
      selectedScreen!.origin_y_cm = conversion;
      
      flagHardwareChange();
    } else {
      showError('You must input a positive number for Y origin');
    }
  };
  const screenWidthChanged = (width: string) => {
    const conversion = parseFloat(width);
    if (validateDimensions(conversion) && conversion > 0.0) {
      selectedScreen!.width_cm = conversion;
      
      flagHardwareChange();
    } else {
      showError('You must input a number greater than 0.0 locker for width');
    }
  };
  const screenHeightChanged = (height: string) => {
    const conversion = parseFloat(height);
    if (validateDimensions(conversion) && conversion > 0.0) {
      selectedScreen!.height_cm = conversion;
      
      flagHardwareChange();
    } else {
      showError('You must input a number greater than 0.0 for height');
    }    
  };
  const screenLocationChanged = (location: string) => {
    selectedScreen!.location = location;
    
    flagHardwareChange();
  };
  const screenRoleChanged = (screenRole: string) => {
    selectedScreen!.role = screenRole;
    
    flagHardwareChange();
  };
  const screenConnectionChanged = (connection: string) => {
    selectedScreen!.connection = connection;
    flagHardwareChange();
  };
  //#endregion

  //#region Input Handling
  const onPucClicked = (pucId: string) => {
    // resetLockerColors();

    const puc = pucs.find(x => x.id === pucId);
    if (puc) {
      setSelectedPuc(puc);
      setSelectedPucId(pucId);
      
      setSelectedLockerId('');
      setSelectedBoardId('');
      setSelectedScreenId('');
    }
  };

  const onBoardClicked = (boardId: string) => {
    const board = boards.find(x => x.id === boardId);
    if (board ) {
      setSelectedBoard(board);
      setSelectedBoardId(boardId);
      setSelectedPucId('');
      setSelectedLockerId('');
      setSelectedScreenId('');
    }
  };

  const onLockerClicked = (lockerId: string) => {
    const locker = lockers.find(x => x.id === lockerId);
    if (locker) {
      setSelectedLocker(locker);
      setSelectedLockerId(locker!.id);
      
      setSelectedPucId('');
      setSelectedBoardId('');
      setSelectedScreenId('');
      
      if (locker!.doors.find(x => x.location === 'front')) {
        setSelectedLockerFrontDoor(true);
      } else {
        setSelectedLockerFrontDoor(false);
      }
      if (locker!.doors.find(x => x.location === 'back')) {
        setSelectedLockerBackDoor(true);
      } else {
        setSelectedLockerBackDoor(false);
      }
      
      setSelectedLockerLabel(locker!.label);
    }
  };

  const onScreenClicked = (screenId: string) => {
    const screen = screens.find(x => x.id.toString() === screenId);
    if (screen) {
      setSelectedScreen(screen);
      setSelectedScreenId(screenId);

      setSelectedLockerId('');
      setSelectedPucId('');
      setSelectedBoardId('');
    }
  };

  const addScreenToPuc = (puc: Puc) => {
    const newScreen = new Screen();
    
    newScreen.id = Math.floor(Math.random() * 100);
    newScreen.puc_id = puc.id;
    if (screens.length !== 0) {
      newScreen.location = screens[0].location === 'front' ? 'back' : 'front';
      newScreen.role = screens[0].role === 'pickup' ? 'management' : 'pickup';
      newScreen.connection = screens[0].connection === 'primary' ? 'secondary' : 'primary';
    }

    screens.push(newScreen);
    puc.screens.push(newScreen);
    setSelectedPucId('');
    flagHardwareChange();
    setSelectedScreen(newScreen);
    setSelectedScreenId(newScreen.id.toString());
  };

  const addBoardToPuc = (puc: Puc) => {
    const nextAddress = Math.max(...puc.boards.map(x => x.slave_address));
    
    const newBoard = new Board();
    newBoard.id = getSlugId();
    newBoard.puc_id = puc.id;
    newBoard.slave_address = nextAddress + 1;
    newBoard.serial = "CHANGEME";

    puc.boards.push(newBoard);
    boards.push(newBoard);
    setSelectedPucId('');
    flagHardwareChange();
    setSelectedBoard(newBoard);
    setSelectedBoardId(newBoard.id);
  };

  const addLockerToBoard = (board: Board) => {
    const newLocker = new Locker();
    newLocker.label = '?';
    newLocker.id = getSlugId();
    newLocker.board_id = board.id;
    const indeces = board!.lockers.map(x => x.hardware_index);
    const missingIndex = [0,1,2,3].find(x => !indeces.includes(x));
    newLocker.hardware_index = missingIndex!;

    const newDoor = new Door();
    newDoor.id = getSlugId();
    newDoor.locker_id = newLocker.id;
    newDoor.location = 'front';
    newLocker.doors.push(newDoor);

    board.lockers.push(newLocker);
    lockers.push(newLocker);
    
    setSelectedBoardId('');
    flagHardwareChange();
    setSelectedLocker(newLocker);
    setSelectedLockerId(newLocker.id);
  };
  //#endregion

  //#region Error Renderer
  const renderErrorAlert = () => {
    if (!errorMessage) {
      return <div></div>;
    } else {
      return <Alert severity='error'>
        <AlertTitle>Error</AlertTitle>
        <strong>{errorMessage}</strong>
      </Alert>;
    }
  };
  //#endregion Error Renderer

  //#region Breadcrumb Renderer
  const showDeletePuc = () => {
    if (pucs.length > 1) {
      return <Tooltip title="Delete Puc">
        <Fab
          size="small"
          aria-label="add"
          onClick={() => {
            showDeletePopup(true);
          }}
          sx={{
            backgroundColor: '#FF0000',
            marginRight: '15px',
            minWidth: '25px',
            minHeight: '25px',
            color: 'white'
          }}>
          <DeleteIcon />
        </Fab>
      </Tooltip>;
    }
  };

  const renderBreadCrumbs = () => {
    if (selectedPucId) {
      return <div>
        <Typography>PUC List</Typography>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          marginBottom: '15px' }}>
          <Select
            sx={{
              color: 'black',
              width: '200px',
              height: '40px',
              marginRight: '15px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'black !important',
              },
              '& .MuiSelect-iconOutlined': {
                color: 'black'
              }
            }}
            value={selectedPucId}
            onChange={(e) => {
              onPucClicked(e.target.value);
            }}>
            {pucs.map((puc) => (
              <MenuItem
                key={puc.id}
                value={puc.id}>
                  {puc!.serial}
              </MenuItem>
            ))}
          </Select>
          {showDeletePuc()}
        </div>
      </div>;
    }

    if (selectedScreenId) {
      const puc = pucs.find(x => x.id === selectedScreen!.puc_id);
      return <Breadcrumbs aria-label="breadcrumb">
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => {
            onPucClicked(selectedScreen!.puc_id);
        }}>PUC ({puc!.serial})</Link>
        <Link color="text.primary">{selectedScreen!.location} Screen</Link>
        <Tooltip title="Delete Screen">
        <Fab
          size="small"
          aria-label="add"
          onClick={() => {
            showDeletePopup(true);
          }}
          sx={{
            backgroundColor: '#FF0000',
            marginRight: '15px',
            minWidth: '25px',
            minHeight: '25px',
            color: 'white'
          }}>
          <DeleteIcon />
        </Fab>
      </Tooltip>
      </Breadcrumbs>;
    }

    if (selectedBoardId) {
      const puc = pucs.find(x => x.id === selectedBoard!.puc_id);
      return <Breadcrumbs aria-label="breadcrumb">
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => {
            onPucClicked(selectedBoard!.puc_id);
        }}>PUC ({puc!.serial})</Link>
        <Link color="text.primary">Board {selectedBoard!.slave_address}</Link>
        <Tooltip title="Delete Board">
        <Fab
          size="small"
          aria-label="add"
          onClick={() => {
            showDeletePopup(true);
          }}
          sx={{
            backgroundColor: '#FF0000',
            marginRight: '15px',
            minWidth: '25px',
            minHeight: '25px',
            color: 'white'
          }}>
          <DeleteIcon />
        </Fab>
      </Tooltip>
      </Breadcrumbs>;
    }

    if (selectedLockerId) {
      const board = boards.find(x => x.id === selectedLocker!.board_id);
      const puc = pucs.find(x => x.id === board!.puc_id);
      return <Breadcrumbs aria-label="breadcrumb">
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => {
            onPucClicked(board!.puc_id);
        }}>PUC ({puc!.serial})</Link>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => {
            onBoardClicked(board!.id);
        }}>Board {board!.slave_address}</Link>
        <Link color="text.primary">Locker {selectedLockerLabel}</Link>
        <Tooltip title="Delete Locker">
          <Fab
            size="small"
            aria-label="add"
            onClick={() => {
              showDeletePopup(true);
            }}
            sx={{
              backgroundColor: '#FF0000',
              marginRight: '15px',
              minWidth: '25px',
              minHeight: '25px',
              color: 'white'
            }}>
            <DeleteIcon />
          </Fab>
        </Tooltip>
      </Breadcrumbs>;
    }
  };
  //#endregion

  //#region Puc Editor Renderer
  const renderAddScreen = () => {
    const otherPucs = pucs.filter(x => x.id !== selectedPucId);
    let screenExists = false;
    const otherScreens = otherPucs.map(x => x.screens);
    for (const screen of otherScreens) {
      screenExists = screen.length > 0;
    }
    
    if (screenExists) {
      return <div>This is an addon since another PUC has a screen.</div>;
    } else {
      if (screens.length >= 2) {
        return <div></div>;
      } else {
        return <div style={{
          width: '250px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          marginBottom: '15px',
          }}>
          <Fab
            size="small"
            color="primary"
            aria-label="add"
            onClick={() => {
              addScreenToPuc(selectedPuc!);
            }}
            sx={{
              marginRight: '15px',
              minWidth: '25px',
              minHeight: '25px'
            }}>
            <AddIcon />
          </Fab>
          <div>Add Screen</div>
        </div>;
      }
    }
  };

  const renderPucEditor = () => {
    if (!selectedPucId) {
      return <div></div>;
    } else {
      return <Box 
          sx={{
            py: 2,
            px: 3,
            width: '500px',
            display: 'flex',
            pt: 0,
            flexDirection: 'column' }}>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              marginBottom: '15px' }}>
              <Fab
                size="small"
                color="primary"
                aria-label="add"
                onClick={() => {
                  setDialog(true);
                }}
                sx={{
                  marginRight: '15px',
                  minWidth: '25px',
                  minHeight: '25px'
                }}>
                <AddIcon />
              </Fab>
              <div>Add PUC</div>
            </div>
            <TextField
              inputProps={{ maxLength: 16 }}
              sx={{
                width: '200px',
                marginBottom: '15px',
                marginTop: '10px'
              }}
              label='Serial'
              value={selectedPuc?.serial}
              onChange={(e) => {
                pucSerialChanged(e.target.value);
              }} />
            <Typography>PUC Position</Typography>
            <div style={{
              width: '250px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginBottom: '15px',
              marginTop: '15px' }}>
              <TextField
                sx={{
                  width: '110px',
                  marginBottom: '15px'
                }}
                label='Origin X (cm)'
                type="number"
                value={selectedPuc?.origin_x_cm}
                onChange={(e) => {
                  pucOriginXChanged(e.target.value);
                }} />
              <TextField
                sx={{
                  width: '110px',
                  marginBottom: '15px'
                }}
                label='Origin Y (cm)'
                type="number"
                value={selectedPuc?.origin_y_cm}
                onChange={(e) => {
                  pucOriginYChanged(e.target.value);
                }} />
            </div>
            <Typography>PUC Dimensions</Typography>
            <div style={{
              width: '250px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginBottom: '15px',
              marginTop: '15px' }}>
              <TextField
                sx={{
                  width: '100px',
                  marginBottom: '15px'
                }}
                label='Width (cm)'
                type="number"
                value={selectedPuc?.width_cm}
                onChange={(e) => {
                  pucWidthChanged(e.target.value);
                }} />
              <TextField
                sx={{
                  width: '100px',
                  marginBottom: '15px'
                }}
                label='Height (cm)'
                type="number"
                value={selectedPuc?.height_cm}
                onChange={(e) => {
                  pucHeightChanged(e.target.value);
                }} />
            </div>
            <Typography variant="h6">Screens:</Typography>
            {selectedPuc!.screens.map((screen, screenIndex) => (
              <Link
                key={screenIndex}
                onClick = {() => {
                  onScreenClicked(screen.id.toString());
                }}>
                  {screen.location} Screen
              </Link>
            ))}
            {renderAddScreen()}
            <Typography
              variant="h6"
              sx={{
                marginTop: '10px'
              }}>Boards:</Typography>
            {selectedPuc!.boards.map((board, boardIndex) => (
              <Link
                key={boardIndex}
                onClick = {() => {
                  onBoardClicked(board.id);
                }}>
                  Board {board.slave_address}
              </Link>
            ))}
            <div style={{
              width: '250px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              marginBottom: '15px',
              }}>
              <Fab
                size="small"
                color="primary"
                aria-label="add"
                onClick={() => {
                  addBoardToPuc(selectedPuc!);
                }}
                sx={{
                  marginTop: '5px',
                  marginRight: '5px',
                  minWidth: '25px',
                  minHeight: '25px'
                }}>
                <AddIcon />
              </Fab>
              <div>Add Board</div>
            </div>
        </Box>;
    }
  };
  //#endregion

  //#region Locker Editor Renderer
  const renderLockerEditor = () => {
    if (!selectedLockerId) {
      return <div></div>;
    } else {
      return <ListItem sx={{ py: 2, px: 3 }}>
          <Box 
            sx={{
              height: '300px',
              width: '500px',
              display: 'flex',
              flexDirection: 'column' }}>
            <div style={{
              width: '250px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginBottom: '15px' }}>
              <TextField
                inputProps={{ maxLength: 24 }}
                sx={{
                  width: '100px',
                  marginBottom: '15px',
                  marginTop: '5px'
                }}
                label='Label'
                value={selectedLockerLabel}
                onChange={(e) => {
                  changeLockerLabel(e.target.value);
                }} />
              <TextField
                inputProps={{ maxLength: 3 }}
                sx={{
                  width: '120px',
                  marginBottom: '15px',
                  marginTop: '5px'
                }}
                type="number"
                label='Hardware Index'
                value={selectedLocker?.hardware_index}
                onChange={(e) => {
                  changeHardwareIndex(e.target.value);
                }} />
            </div>
            <div style={{
              width: '200px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginBottom: '15px'}}>
              <div style={{
                display: 'flex',
                flexDirection: 'column'}}>
                <Typography>Enabled</Typography>
                <Switch
                  checked={selectedLocker?.enabled}
                  onChange={(e) => {
                    enableChanged(e.target.checked);
                  }} />
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column' }}>
                <Typography>Accessible</Typography>
                <Switch
                  checked={selectedLocker?.accessible}
                  onChange={(e) => {
                    accessibleChanged(e.target.checked);
                  }} />
              </div>
            </div>
            <Typography>Locker Position</Typography>
            <div style={{
              width: '250px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginBottom: '15px',
              marginTop: '15px' }}>
              <TextField
                sx={{
                  width: '110px',
                  marginBottom: '15px'
                }}
                label='Origin X (cm)'
                type="number"
                value={selectedLocker?.origin_x_cm}
                onChange={(e) => {
                  lockerOriginXChanged(e.target.value);
                }} />
              <TextField
                sx={{
                  width: '110px',
                  marginBottom: '15px'
                }}
                label='Origin Y (cm)'
                type="number"
                value={selectedLocker?.origin_y_cm}
                onChange={(e) => {
                  lockerOriginYChanged(e.target.value);
                }} />
            </div>
            <Typography>Locker Dimensions</Typography>
            <div style={{
              width: '250px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginBottom: '15px',
              marginTop: '15px' }}>
              <TextField
                sx={{
                  width: '100px',
                  marginBottom: '15px'
                }}
                label='Width (cm)'
                type="number"
                value={selectedLocker?.width_cm}
                onChange={(e) => {
                  lockerWidthChanged(e.target.value);
                }} />
              <TextField
                sx={{
                  width: '100px',
                  marginBottom: '15px'
                }}
                label='Height (cm)'
                type="number"
                value={selectedLocker?.height_cm}
                onChange={(e) => {
                  lockerHeightChanged(e.target.value);
                }} />
            </div>
            <Typography>Locker Type</Typography>
            <Select
              sx={{
                color: 'black', 
                width: '200px', 
                height: '40px',
                marginBottom: '40px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'black !important',
                },
                '& .MuiSelect-iconOutlined': {
                  color: 'black'
                }
              }} 
              value={selectedLocker?.type}
              onChange={(e) => {
                lockerTypeChanged(e.target.value);
              }}
              required>
              {['ambient', 'heated'].map((type, index) => (
                <MenuItem 
                  key={index}
                  value={type}>
                    {type}
                </MenuItem>
              ))}
            </Select>
            <FormControl
              required
              component="fieldset"
              variant="standard">
              <FormLabel component="legend">Doors (pick one or both)</FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox checked={selectedLockerFrontDoor} onChange={(e) => {
                      doorsModified(e.target.checked, 'front');
                    }} name="frontDoor" />
                  }
                  label="Front Door" />
                <FormControlLabel
                  control={
                    <Checkbox checked={selectedLockerBackDoor} onChange={(e) => {
                      doorsModified(e.target.checked, 'back');
                    }} name="backDoor" />
                  }
                  label="Back Door" />
              </FormGroup>
            </FormControl>
          </Box>
        </ListItem>;
    }
  };
  //#endregion

  //#region Screen Editor Renderer
  const renderScreenEditor = () => {
    if (!selectedScreenId) {
      return <div></div>;
    } else {
      return <ListItem sx={{ py: 2, px: 3 }}>
          <Box 
            sx={{
              height: '300px',
              width: '500px',
              display: 'flex',
              flexDirection: 'column' }}>
            <Typography>Screen Position</Typography>
            <div style={{
              width: '250px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginBottom: '15px',
              marginTop: '15px' }}>
              <TextField
                sx={{
                  width: '110px',
                  marginBottom: '15px'
                }}
                label='Origin X (cm)'
                type="number"
                value={selectedScreen?.origin_x_cm}
                onChange={(e) => {
                  screenOriginXChanged(e.target.value);
                }} />
              <TextField
                sx={{
                  width: '110px',
                  marginBottom: '15px'
                }}
                label='Origin Y (cm)'
                type="number"
                value={selectedScreen?.origin_y_cm}
                onChange={(e) => {
                  screenOriginYChanged(e.target.value);
                }} />
            </div>
            <Typography>Screen Dimensions</Typography>
            <div style={{
              width: '250px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginBottom: '15px',
              marginTop: '15px' }}>
              <TextField
                sx={{
                  width: '100px',
                  marginBottom: '15px'
                }}
                label='Width (cm)'
                type="number"
                value={selectedScreen?.width_cm}
                onChange={(e) => {
                  screenWidthChanged(e.target.value);
                }} />
              <TextField
                sx={{
                  width: '100px',
                  marginBottom: '15px'
                }}
                label='Height (cm)'
                type="number"
                value={selectedScreen?.height_cm}
                onChange={(e) => {
                  screenHeightChanged(e.target.value);
                }} />
            </div>
            <Typography>Screen Location</Typography>
            <Select
              sx={{
                color: 'black', 
                width: '200px', 
                height: '40px',
                marginBottom: '40px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'black !important',
                },
                '& .MuiSelect-iconOutlined': {
                  color: 'black'
                }
              }} 
              value={selectedScreen?.location}
              onChange={(e) => {
                screenLocationChanged(e.target.value);
              }}
              required>
              {['front', 'back'].map((type, index) => (
                <MenuItem 
                  key={index}
                  value={type}>
                    {type}
                </MenuItem>
              ))}
            </Select>
            <Typography>Screen Role</Typography>
            <Select
              sx={{
                color: 'black', 
                width: '200px', 
                height: '40px',
                marginBottom: '40px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'black !important',
                },
                '& .MuiSelect-iconOutlined': {
                  color: 'black'
                }
              }} 
              value={selectedScreen?.role}
              onChange={(e) => {
                screenRoleChanged(e.target.value);
              }}
              required>
              {['pickup', 'management'].map((type, index) => (
                <MenuItem 
                  key={index}
                  value={type}>
                    {type}
                </MenuItem>
              ))}
            </Select>
            <Typography>Screen Connection</Typography>
            <Select
              sx={{
                color: 'black', 
                width: '200px', 
                height: '40px',
                marginBottom: '40px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'black !important',
                },
                '& .MuiSelect-iconOutlined': {
                  color: 'black'
                }
              }} 
              value={selectedScreen?.connection}
              onChange={(e) => {
                screenConnectionChanged(e.target.value);
              }}
              required>
              {['primary', 'secondary'].map((type, index) => (
                <MenuItem 
                  key={index}
                  value={type}>
                    {type}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </ListItem>;
    }
  };
  //#endregion

  //#region Board Editor Renderer
  const renderAddLocker = () => {
    if (selectedBoard!.lockers.length < 4) {
      return <div style={{
        width: '250px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: '15px',
        marginTop: '15px' }}>
        <Fab
          size="small"
          color="primary"
          aria-label="add"
          onClick={() => {
            addLockerToBoard(selectedBoard!);
          }}
          sx={{
            marginRight: '15px',
            minWidth: '25px',
            minHeight: '25px'
          }}>
          <AddIcon />
        </Fab>
        <div>Add Locker</div>
      </div>;
    }
  };

  const renderBoardEditor = () => {
    if (!selectedBoardId) {
      return <div></div>;
    } else {
      return <ListItem sx={{ py: 2, px: 3 }}>
        <Box 
          sx={{
            height: '400px',
            width: '500px',
            display: 'flex',
            flexDirection: 'column' }}>
            <TextField
              sx={{
                width: '100px',
                marginBottom: '15px'
              }}
              label='Serial'
              value={selectedBoard?.serial}
              onChange={(e) => { boardSerialChanged(e.target.value);}} />
            <TextField
              inputProps={{ maxLength: 3 }}
              sx={{
                width: '100px',
                marginBottom: '15px'
              }}
              label='Slave Address'
              value={selectedBoard?.slave_address}
              onChange={(e) => { boardSlaveAddressChanged(e.target.value); }} />
            <TextField
              inputProps={{ maxLength: 24 }}
              sx={{
                width: '120px',
                marginBottom: '15px'
              }}
              label='Model'
              value={selectedBoard?.model}
              onChange={(e) => { boardModelChanged(e.target.value); }} />
            <div style={{
              display: 'flex',
              flexDirection: 'column'}}>
            <Typography>Lockers Have Product Sensor</Typography>
              <Switch
                checked={selectedBoard?.lockers_have_product_sensor}
                onChange={(e) => {
                  boardProductSensorChanged(e.target.checked);
                }} />
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column'}}>
            <Typography>Lockers Have Motorized Close</Typography>
              <Switch
                checked={selectedBoard?.lockers_have_motorized_close}
                onChange={(e) => {
                  boardMotorizedChanged(e.target.checked);
                }} />
            </div>
            {selectedBoard!.lockers.map((locker, lockerIndex) => (
              <Link
                key={lockerIndex}
                onClick = {() => {
                  onLockerClicked(locker.id);
                }}>
                  Locker {locker.label}
              </Link>
            ))}
            {renderAddLocker()}
        </Box>
      </ListItem>;
    }
  };
  //#endregion

  useEffect(() => {
    //#region Reset
    setLockers([]);
    setBoards([]);
    setScreens([]);
    //#endregion

    const tempLockers: Array<Locker> = [];
    const tempBoards: Array<Board> = [];
    const tempScreens: Array<Screen> = [];

    for (const puc of pucs) {
      for (const screen of puc.screens || []) {
        tempScreens.push(screen);
      }
      
      for (const board of puc.boards) {
        tempBoards.push(board);
        for (const locker of board.lockers) {
          tempLockers.push(locker);
        }
      }
    }
    //#endregion

    setLockers(tempLockers);
    setBoards(tempBoards);
    setScreens(tempScreens);

    if (pucs.length === 0) {
      alert('No PUCs found in this cabinet, press OK to select a new one');
      setForceSelectMain(true);
      setDialog(true);
    } else {
      try {
        if (selectedScreenId) {
          onScreenClicked(selectedScreenId);
        } else if (selectedLockerId) {
          onLockerClicked(selectedLockerId);
        } else if (selectedBoardId) {
          onBoardClicked(selectedBoardId);
        } else if (selectedPucId) {
          onPucClicked(selectedPucId);
        } else if (pucs[0].id) {
          onPucClicked(pucs[0].id);
        }
      } catch(err) {
        alert(err);
      }
    }

  }, [ hardwareChanged ]);

  const addNewPuc = (newPuc: Puc, side: string) => {
    let nextAddress = Math.max(...boards.map(x => x.slave_address));
    for (const board of newPuc.boards) {
      board.slave_address = nextAddress + 1;
      nextAddress++;
    }
    if (side === 'left') {
      newPuc.origin_x_cm = 0;
      
      let lastPuc = newPuc;
      for (let i = 0; i < pucs.length; i++) {
        pucs[i].origin_x_cm = lastPuc.origin_x_cm + lastPuc.width_cm + 10;
        lastPuc = pucs[i];
      }
    } else {
      const sorted = pucs.sort((a, b) => a.origin_x_cm - b.origin_x_cm);
      const lastPuc = sorted[sorted.length - 1];
      newPuc.origin_x_cm = lastPuc.origin_x_cm + lastPuc.width_cm + 10;
    }
    
    pucs.push(newPuc);
    boards.push(newPuc.boards[0]);
    lockers.push(newPuc.boards[0].lockers[0]);
    
    flagHardwareChange();
    setSelectedPuc(newPuc);
    setSelectedPucId(newPuc.id);
    setForceSelectMain(false);
    setDialog(false);
  };

  return (
    <div className=" text-center text-dark">
      <Dialog
        sx={{
          marginLeft: '20%',
          width: '70%'
        }}
        fullWidth
        maxWidth='lg'
        open={dialog}
        onClose={() => setDialog(false)}>
        <AddPucDialog setDialog={setDialog} finish={addNewPuc} selectMainPuc={forceSelectMain} />
      </Dialog>
      <Drawer
        variant="permanent"
        anchor="right"
        ModalProps={{
          keepMounted: false,
        }}
        PaperProps={{
          style: {
            position: "absolute",
            width: "35%",
            background: "white"
          }
        }}>
        <List disablePadding>
          <ListItem sx={{ ...item, ...itemCategory, fontSize: 22, color: 'black' }}>
            {renderBreadCrumbs()}
          </ListItem>
          <ListItem>
          {renderErrorAlert()}
          </ListItem>
          {renderLockerEditor()}
          {renderPucEditor()}
          {renderBoardEditor()}
          {renderScreenEditor()}
        </List>
      </Drawer>
      <CabinetRenderer
        pucs={pucs}
        canvasWidth={canvasWidth}
        canvasHeight={canvasHeight}
        onPucSelected={onPucClicked}
        onBoardSelected={onBoardClicked}
        onScreenSelected={onScreenClicked}
        onLockerSelected={onLockerClicked}
        hardwareChanged={hardwareChanged}
        enableInput={true} />
      <Dialog
        open={showDeleteDialog}
        onClose={()=>{
          showDeletePopup(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this item? It cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>{
            showDeletePopup(false);
          }}>Cancel</Button>
          <Button onClick={()=>{
            handleDelete();
          }} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default PucEditor;
